import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType, FileType, artifactTypeFromJSON } from "build-ts/common";
import { AppIcons } from "constants/icons";
import { ArtifactOpenOpts, SelectedArtifactItem } from "modules/interfaces";
import { IconType } from "react-icons/lib/iconBase";
import { TAB_ID } from "../dataframe/utils/dataFrameCodeUtils";
import { ARTIFACT_TAG_TYPE, buildWorkflowTag } from "modules/chat/utils/chatUtils";

/**
 * Discriminant type for the nested artifact registry's outer key.
 * Derived from ARTIFACT_TAG_TYPE so it stays in sync automatically when new
 * tag types are added — no manual update needed here.
 *
 *   "code"         — DATAFRAME_ANALYSIS top-level (the analysis itself)
 *   "dataframe"    — pandas_df sub-resources + KG_DATAFRAME
 *   "chart"        — chart sub-resources
 *   "report"       — report sub-resources
 *   "workflow"     — SCENARIO_JOB
 *   "workflow-run" — ANALYSIS_OUTPUT
 */
export type ArtifactKind = (typeof ARTIFACT_TAG_TYPE)[keyof typeof ARTIFACT_TAG_TYPE];

/**
 * Nested artifact registry — mirrors the backend's map<artifact_type, map<artifact_name, Artifact>>.
 * Using a nested map ensures that artifacts of different types can share the same name
 * without one overwriting the other (e.g. a "code" artifact and a "dataframe" artifact both
 * named "results" are stored and listed separately).
 */
export type ArtifactMap = Map<ArtifactKind, Map<string, ArtifactProto>>;

/**
 * Returns a stable identity string for an artifact, derived from its header id.
 * Used for deduplication and caching across allArtifacts and selectedArtifacts.
 */
export function getArtifactHeaderId(artifact: ArtifactProto): string {
  if (!artifact) return "";
  return (
    artifact.dataframe_analysis?.header?.id ||
    artifact.kg_dataframe?.header?.id ||
    artifact.scenario_job?.header?.id ||
    artifact.analysis_output?.header?.id ||
    artifact.file_artifact?.header?.id ||
    artifact.workflow_run?.header?.id ||
    artifact.workflow_run_search?.header?.id ||
    ""
  );
}

/**
 * Registry key for primary entries in ArtifactMap.
 * FILE_ARTIFACT uses header.id because sanitized names can repeat across uploads
 * (e.g. two uploads of "memory_id.csv" both get header.name "memory_id").
 */
export function getArtifactMapKey(artifact: ArtifactProto): string {
  if (getArtifactType(artifact) === ArtifactType.FILE_ARTIFACT) {
    const fileType = artifact.file_artifact?.file_type;
    if (fileType === FileType.CHART_SPEC_JSON || fileType === FileType.DOCX) {
      return getArtifactHeaderName(artifact);
    }
    return getArtifactHeaderId(artifact) || getArtifactHeaderName(artifact);
  }
  return getArtifactHeaderName(artifact);
}

/**
 * Resolve the latest registered copy of an artifact from the session store.
 * FILE_ARTIFACT lookups use header.id; all other types use header.name.
 */
export function resolveRegisteredArtifact(
  artifact: ArtifactProto,
  lookupByName: (name: string, kind?: ArtifactKind) => ArtifactProto | undefined,
  opts?: { kind?: ArtifactKind; subResourceName?: string }
): ArtifactProto {
  const type = getArtifactType(artifact);
  if (type === ArtifactType.FILE_ARTIFACT) {
    const kind = getArtifactKind(artifact);
    if (kind === ARTIFACT_TAG_TYPE.file) {
      const id = getArtifactHeaderId(artifact);
      if (id) {
        return lookupByName(id, kind) ?? artifact;
      }
    } else {
      const name = getArtifactHeaderName(artifact);
      if (name) {
        return lookupByName(name, kind) ?? artifact;
      }
    }
    return artifact;
  }
  if (type === ArtifactType.WORKFLOW_RUN && opts?.kind && opts?.subResourceName) {
    return lookupByName(opts.subResourceName, opts.kind) ?? artifact;
  }
  return lookupByName(getArtifactHeaderName(artifact), opts?.kind) ?? artifact;
}

/**
 * Returns the artifact's header name.
 * Used as the key for the name-based artifact map and name index.
 */
export function getArtifactHeaderName(artifact: ArtifactProto): string {
  if (!artifact) return "";
  return (
    artifact.dataframe_analysis?.header?.name ||
    artifact.kg_dataframe?.header?.name ||
    artifact.scenario_job?.scheduled_analysis?.darwin_name ||
    artifact.analysis_output?.header?.name ||
    artifact.file_artifact?.header?.name ||
    artifact.workflow_run?.header?.name ||
    artifact.workflow_run_search?.header?.name ||
    ""
  );
}

/**
 * Safe wrapper around artifactTypeFromJSON that handles undefined artifacts.
 * Replaces the `artifactTypeFromJSON(a?.artifact_type ?? "0")` pattern throughout the codebase.
 */
export function getArtifactType(artifact: ArtifactProto): ArtifactType {
  return artifactTypeFromJSON(artifact?.artifact_type);
}

/**
 * Maps an artifact to its ArtifactKind bucket key — the same key used in ArtifactMap.
 * Use this when you already hold the artifact and need to call lookupByName with the
 * correct kind for a precise (type, name) lookup.
 */
export function getArtifactKind(artifact: ArtifactProto): ArtifactKind {
  const type = getArtifactType(artifact);
  if (type === ArtifactType.KG_DATAFRAME) return ARTIFACT_TAG_TYPE.dataframe;
  if (type === ArtifactType.DATAFRAME_ANALYSIS) return ARTIFACT_TAG_TYPE.code;
  if (type === ArtifactType.SCENARIO_JOB) return ARTIFACT_TAG_TYPE.workflow;
  if (type === ArtifactType.FILE_ARTIFACT) {
    const fileType = artifact.file_artifact?.file_type;
    if (fileType === FileType.CHART_SPEC_JSON) return ARTIFACT_TAG_TYPE.chart;
    if (fileType === FileType.DOCX) return ARTIFACT_TAG_TYPE.report;
    return ARTIFACT_TAG_TYPE.file;
  }
  if (type === ArtifactType.WORKFLOW_RUN) return ARTIFACT_TAG_TYPE.workflowRunDetail;
  if (type === ArtifactType.WORKFLOW_RUN_SEARCH) return ARTIFACT_TAG_TYPE.workflowRunSearch;
  return ARTIFACT_TAG_TYPE.workflowRun;
}

/** WorkflowRunCard tab ids — keep in sync with workflow/WorkflowRunCard.tsx TAB. */
export const WORKFLOW_RUN_TAB = {
  outputs: "outputs",
  inputs: "inputs",
  synthesis: "synthesis",
} as const;

/** True when a nested FILE_ARTIFACT lacks lazy-loaded content (CSV data or chart spec data). */
export function fileArtifactNeedsHydration(artifact: ArtifactProto): boolean {
  if (getArtifactType(artifact) !== ArtifactType.FILE_ARTIFACT) return false;
  const fa = artifact.file_artifact;
  if (!fa) return false;
  if (fa.file_type === FileType.CSV) return !fa.pandas_dataframe?.data_json;
  if (fa.file_type === FileType.CHART_SPEC_JSON) {
    const raw = fa.chart_spec_json?.trim();
    if (!raw) return true;
    try {
      return (JSON.parse(raw) as Record<string, unknown>).data === undefined;
    } catch {
      return true;
    }
  }
  return false;
}

/** True when any nested input/output FileArtifact on a workflow run needs hydration. */
export function workflowRunNeedsHydration(artifact: ArtifactProto): boolean {
  if (getArtifactType(artifact) !== ArtifactType.WORKFLOW_RUN || !artifact.workflow_run) return false;
  const nested = [...(artifact.workflow_run.input_artifacts ?? []), ...(artifact.workflow_run.output_artifacts ?? [])];
  return nested.some(fileArtifactNeedsHydration);
}

function workflowRunSubResourceInInputs(run: { input_artifacts?: ArtifactProto[] }, name: string): boolean {
  const trimmed = name.trim();
  return (run.input_artifacts ?? []).some(a => getArtifactHeaderName(a).trim() === trimmed);
}

/** Build persisted panel view state for a workflow run tab + optional nested pill. */
export function buildWorkflowRunViewState(
  artifact: ArtifactProto,
  activeTab?: string,
  subResourceName?: string
): {
  activeTab: string;
  selectedOutputPillName: string;
  selectedInputPillName?: string;
} {
  const run = artifact.workflow_run;
  const defaultTab = activeTab ?? WORKFLOW_RUN_TAB.outputs;
  if (!subResourceName || !run) {
    return { activeTab: defaultTab, selectedOutputPillName: "" };
  }
  if (workflowRunSubResourceInInputs(run, subResourceName)) {
    return { activeTab: WORKFLOW_RUN_TAB.inputs, selectedOutputPillName: "", selectedInputPillName: subResourceName };
  }
  return { activeTab: WORKFLOW_RUN_TAB.outputs, selectedOutputPillName: subResourceName };
}

/**
 * Returns the artifact's tab key, used to identify the artifact tab.
 */
export function getArtifactTabKey(item: SelectedArtifactItem): string {
  const type = item.artifact ? artifactTypeFromJSON(item.artifact.artifact_type) : null;
  if (type === ArtifactType.ANALYSIS_OUTPUT && item.analysisGroupId) {
    return `group-${item.analysisGroupId}`;
  }
  return `item-${item.orderIndex}`;
}

/**
 * Returns the artifact's label, normalized to lowercase+trimmed.
 * Used as the label for the artifact tab.
 */
export function getArtifactLabel(item: ArtifactProto): string {
  const type = artifactTypeFromJSON(item.artifact_type);
  if (type === ArtifactType.KG_DATAFRAME) return item.kg_dataframe?.header?.name ?? "DataFrame";
  if (type === ArtifactType.SCENARIO_JOB) return item.scenario_job?.header?.name ?? "Scenario";
  if (type === ArtifactType.KG_OBSERVATION) return "KG Observation";
  if (type === ArtifactType.DATAFRAME_ANALYSIS) return item.dataframe_analysis?.header?.name ?? "DF Analysis";
  if (type === ArtifactType.ANALYSIS_OUTPUT) return "Analysis";
  if (type === ArtifactType.FILE_ARTIFACT) return item.file_artifact?.header?.name ?? "Uploaded File";
  if (type === ArtifactType.WORKFLOW_RUN)
    return item.workflow_run?.header?.description ?? item.workflow_run?.header?.name ?? "Workflow Run";
  if (type === ArtifactType.WORKFLOW_RUN_SEARCH) return item.workflow_run_search?.header?.name ?? "Run Search";
  return "Artifact";
}

/** Lowercase file extension for message/file chips (e.g. CSV → "csv"). */
function fileTypeChipExtension(fileType: FileType): string | undefined {
  if (fileType === FileType.CSV) {
    return "csv";
  }
  if (fileType === FileType.UNKNOWN_FILE_TYPE) {
    return undefined;
  }
  const enumName = FileType[fileType];
  if (typeof enumName === "string") {
    return enumName.toLowerCase();
  }
  return undefined;
}

/** Chip label for an uploaded file — ``{header.name}.{ext}`` (e.g. ``memory.csv``). */
export function getFileArtifactChipLabel(artifact: ArtifactProto): string {
  const name = artifact.file_artifact?.header?.name ?? "uploaded_file";
  const fileType = artifact.file_artifact?.file_type ?? FileType.UNKNOWN_FILE_TYPE;
  const ext = fileTypeChipExtension(fileType);
  return ext ? `${name}.${ext}` : name;
}

/** Minimal interface for a Vega View used by the export utilities. */
export interface VegaView {
  toImageURL: (format: string, scale?: number) => Promise<string>;
}

/**
 * Renders a Vega view to a data URL in the requested format.
 *
 * For JPEG, the PNG is first rendered at 2× scale and then composited over a
 * white canvas before encoding, because JPEG has no alpha channel and
 * transparent pixels would otherwise be encoded as black.
 */
export async function vegaViewToDataURL(view: VegaView, format: "png" | "jpeg"): Promise<string> {
  const pngUrl = await view.toImageURL("png", 2);

  if (format === "png") {
    return pngUrl;
  }

  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = pngUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "white";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  // JPEG is always lossy; quality 1.0 minimises compression artefacts.
  return canvas.toDataURL("image/jpeg", 1.0);
}

/**
 * Returns the artifact's icon, used to display the artifact tab.
 */
export function getArtifactIcon(artifact: ArtifactProto): IconType {
  const type = artifactTypeFromJSON(artifact.artifact_type);
  if (type === ArtifactType.KG_DATAFRAME) return AppIcons.database;
  if (type === ArtifactType.DATAFRAME_ANALYSIS) return AppIcons.mathFunction;
  if (type === ArtifactType.ANALYSIS_OUTPUT) return AppIcons.search;
  if (type === ArtifactType.FILE_ARTIFACT) return AppIcons.file;
  if (type === ArtifactType.WORKFLOW_RUN) return AppIcons.workflowRun;
  if (type === ArtifactType.WORKFLOW_RUN_SEARCH) return AppIcons.search;
  return AppIcons.workflow;
}

/**
 * A single entry that represents an artifact (or sub-resource) in a browsable
 * or mention-able list.
 *
 * - `tag`       – the [[type: name]] string inserted into the chat input.
 * - `artifacts` – the actual ArtifactProto(s) to open; usually 1, except for
 *                 ANALYSIS_OUTPUT groups where multiple share a job_id.
 * - `opts`      – optional panel-open options (activeTab, subResourceName).
 */
export type ArtifactEntry = {
  key: string;
  label: string;
  icon: IconType;
  tag: string;
  artifacts: ArtifactProto[];
  opts?: ArtifactOpenOpts;
};

/** A labelled group of ArtifactEntry items (Tables, Charts, Code, Workflows …). */
export type ArtifactSection = {
  id: string;
  label: string;
  items: ArtifactEntry[];
};

/**
 * Derive a flat list of browse/mention sections from the current session's
 * artifact map. Each bucket of the nested ArtifactMap maps directly to one
 * section, eliminating the need to distinguish sub-resource entries by name.
 *
 * Sections are returned in display order: Raw data → Tables → Charts → Documents → Workflows → Workflow Runs → Code.
 * Empty sections are omitted.
 *
 * Workflows are deduplicated by header id; workflow-run artifacts are grouped
 * by job_id so all runs for the same job appear under one entry.
 *
 * Used by both the ArtifactPanel browse menu and the @ mention menu in chat.
 */
export function buildArtifactSections(artifactMap: ArtifactMap): ArtifactSection[] {
  const uploadedFiles: ArtifactEntry[] = [];
  const rawData: ArtifactEntry[] = [];
  const tables: ArtifactEntry[] = [];
  const charts: ArtifactEntry[] = [];
  const code: ArtifactEntry[] = [];
  const documents: ArtifactEntry[] = [];
  // Keyed by job header id — deduplicates if the same SCENARIO_JOB appears under multiple names.
  const workflowsMap = new Map<string, ArtifactEntry>();
  // Keyed by job_id — each key represents one job's complete set of run artifacts.
  const runsMap = new Map<string, { artifacts: ArtifactProto[]; label: string }>();

  for (const [, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.file)?.entries() ?? [])) {
    const llmName = getArtifactHeaderName(artifact);
    uploadedFiles.push({
      key: artifact.file_artifact?.header?.id ?? llmName,
      label: getArtifactLabel(artifact),
      icon: AppIcons.file,
      tag: `[[dataframe: ${llmName}]]`,
      artifacts: [artifact],
    });
  }

  // "dataframe" bucket holds both KG_DATAFRAME (raw data) and pandas_df sub-resources (tables).
  for (const [name, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.dataframe)?.entries() ?? [])) {
    if (getArtifactType(artifact) === ArtifactType.KG_DATAFRAME) {
      rawData.push({
        key: artifact.kg_dataframe?.header?.id ?? name,
        label: getArtifactLabel(artifact),
        icon: AppIcons.database,
        tag: `[[dataframe: ${name}]]`,
        artifacts: [artifact],
      });
    } else if (getArtifactType(artifact) === ArtifactType.WORKFLOW_RUN) {
      const view = buildWorkflowRunViewState(artifact, WORKFLOW_RUN_TAB.outputs, name);
      tables.push({
        key: name,
        label: name,
        icon: AppIcons.dataframe,
        tag: `[[dataframe: ${name}]]`,
        artifacts: [artifact],
        opts: { activeTab: view.activeTab, subResourceName: name, kind: ARTIFACT_TAG_TYPE.dataframe },
      });
    } else {
      tables.push({
        key: name,
        label: name,
        icon: AppIcons.dataframe,
        tag: `[[dataframe: ${name}]]`,
        artifacts: [artifact],
        opts: { activeTab: TAB_ID.results, subResourceName: name },
      });
    }
  }

  for (const [name, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.chart)?.entries() ?? [])) {
    const opts =
      getArtifactType(artifact) === ArtifactType.WORKFLOW_RUN
        ? (() => {
            const view = buildWorkflowRunViewState(artifact, WORKFLOW_RUN_TAB.outputs, name);
            return { activeTab: view.activeTab, subResourceName: name, kind: ARTIFACT_TAG_TYPE.chart };
          })()
        : { activeTab: TAB_ID.results, subResourceName: name };
    charts.push({
      key: name,
      label: name,
      icon: AppIcons.chart,
      tag: `[[chart: ${name}]]`,
      artifacts: [artifact],
      opts,
    });
  }

  for (const [name, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.code)?.entries() ?? [])) {
    code.push({
      key: artifact.dataframe_analysis?.header?.id ?? name,
      label: name || "Analysis",
      icon: AppIcons.codeAlt,
      tag: `[[code: ${name}]]`,
      artifacts: [artifact],
      opts: { activeTab: TAB_ID.code },
    });
  }

  for (const [name, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.report)?.entries() ?? [])) {
    const opts =
      getArtifactType(artifact) === ArtifactType.WORKFLOW_RUN
        ? (() => {
            const view = buildWorkflowRunViewState(artifact, WORKFLOW_RUN_TAB.outputs, name);
            return { activeTab: view.activeTab, subResourceName: name, kind: ARTIFACT_TAG_TYPE.report };
          })()
        : { activeTab: TAB_ID.results, subResourceName: name };
    documents.push({
      key: name,
      label: name,
      icon: AppIcons.document,
      tag: `[[report: ${name}]]`,
      artifacts: [artifact],
      opts,
    });
  }

  for (const [, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.workflow)?.entries() ?? [])) {
    const id = artifact.scenario_job?.header?.id!;
    const darwinName = artifact.scenario_job?.scheduled_analysis?.darwin_name;
    const headerName = getArtifactLabel(artifact);
    workflowsMap.set(id, {
      key: id,
      label: headerName,
      icon: AppIcons.workflow,
      tag: buildWorkflowTag(darwinName!, headerName),
      artifacts: [artifact],
    });
  }

  for (const [, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.workflowRun)?.entries() ?? [])) {
    // Group all run artifacts for the same job under one entry, keyed by job_id.
    const id = `ao:${artifact.analysis_output?.job_id!}`;
    if (!runsMap.has(id)) {
      runsMap.set(id, {
        artifacts: [],
        label: `${workflowsMap.get(artifact.analysis_output?.job_id!)?.label} Runs`,
      });
    }
    runsMap.get(id)!.artifacts.push(artifact);
  }

  const runs: ArtifactEntry[] = Array.from(runsMap.entries()).map(([id, { artifacts, label }]) => ({
    key: id,
    label,
    icon: AppIcons.search as IconType,
    tag: `[[workflow-run: ${label}]]`,
    artifacts,
  }));

  const workflowRunDetails: ArtifactEntry[] = [];
  for (const [, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.workflowRunDetail)?.entries() ?? [])) {
    const name = getArtifactHeaderName(artifact);
    workflowRunDetails.push({
      key: artifact.workflow_run?.header?.id ?? name,
      label: getArtifactLabel(artifact),
      icon: AppIcons.workflowRun,
      tag: `[[workflow-run-detail: ${name}]]`,
      artifacts: [artifact],
    });
  }

  const workflowRunSearchItems: ArtifactEntry[] = [];
  for (const [, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.workflowRunSearch)?.entries() ?? [])) {
    const name = getArtifactHeaderName(artifact);
    workflowRunSearchItems.push({
      key: artifact.workflow_run_search?.header?.id ?? name,
      label: getArtifactLabel(artifact),
      icon: AppIcons.search,
      tag: `[[workflow-run-search: ${name}]]`,
      artifacts: [artifact],
    });
  }

  const sections: ArtifactSection[] = [];
  if (uploadedFiles.length > 0) sections.push({ id: "uploaded-files", label: "Uploaded Tables", items: uploadedFiles });
  if (rawData.length > 0) sections.push({ id: "raw-data", label: "Raw data", items: rawData });
  if (tables.length > 0) sections.push({ id: "tables", label: "Tables", items: tables });
  if (charts.length > 0) sections.push({ id: "charts", label: "Charts", items: charts });
  if (documents.length > 0) sections.push({ id: "documents", label: "Documents", items: documents });
  if (workflowsMap.size > 0)
    sections.push({ id: "workflows", label: "Workflows", items: Array.from(workflowsMap.values()) });
  if (runs.length > 0) sections.push({ id: "runs", label: "Workflow Runs", items: runs });
  if (workflowRunDetails.length > 0)
    sections.push({ id: "workflow-run-details", label: "Run Details", items: workflowRunDetails });
  if (workflowRunSearchItems.length > 0)
    sections.push({ id: "workflow-run-searches", label: "Run Searches", items: workflowRunSearchItems });
  if (code.length > 0) sections.push({ id: "code", label: "Code", items: code });
  return sections;
}

/**
 * Returns the ArtifactOpenOpts for a given tag/link type and name.
 * Used by both Message.tsx (user message tags) and MarkdownRenderer.tsx (AI message links)
 * to ensure consistent tab/pill navigation across all artifact link surfaces.
 */
export function getArtifactOpenOpts(tagType: string, name: string): ArtifactOpenOpts | undefined {
  switch (tagType) {
    case ARTIFACT_TAG_TYPE.code:
      return { activeTab: TAB_ID.code, kind: ARTIFACT_TAG_TYPE.code };
    case ARTIFACT_TAG_TYPE.dataframe:
      return { activeTab: TAB_ID.results, subResourceName: name, kind: ARTIFACT_TAG_TYPE.dataframe };
    case ARTIFACT_TAG_TYPE.chart:
      return { activeTab: TAB_ID.results, subResourceName: name, kind: ARTIFACT_TAG_TYPE.chart };
    case ARTIFACT_TAG_TYPE.report:
      return { activeTab: TAB_ID.results, subResourceName: name, kind: ARTIFACT_TAG_TYPE.report };
    case ARTIFACT_TAG_TYPE.file:
      return { kind: ARTIFACT_TAG_TYPE.file };
    default:
      return undefined;
  }
}

/** Icon and open opts for a resolved [[dataframe:name]] link (file upload, KG table, DFA or run output). */
export function getDataframeTagLinkPresentation(
  artifact: ArtifactProto | undefined,
  name: string
): { icon: (typeof AppIcons)[keyof typeof AppIcons]; opts?: ArtifactOpenOpts } {
  if (artifact && getArtifactType(artifact) === ArtifactType.FILE_ARTIFACT) {
    return { icon: AppIcons.dataframe, opts: getArtifactOpenOpts(ARTIFACT_TAG_TYPE.file, name) };
  }
  if (artifact && getArtifactType(artifact) === ArtifactType.KG_DATAFRAME) {
    return { icon: AppIcons.database, opts: getArtifactOpenOpts(ARTIFACT_TAG_TYPE.dataframe, name) };
  }
  if (artifact && getArtifactType(artifact) === ArtifactType.WORKFLOW_RUN) {
    const view = buildWorkflowRunViewState(artifact, WORKFLOW_RUN_TAB.outputs, name);
    return {
      icon: AppIcons.dataframe,
      opts: { activeTab: view.activeTab, subResourceName: name, kind: ARTIFACT_TAG_TYPE.dataframe },
    };
  }
  return { icon: AppIcons.dataframe, opts: getArtifactOpenOpts(ARTIFACT_TAG_TYPE.dataframe, name) };
}

/** Open opts for chart/report/code links once the target artifact is resolved. */
export function getOpenOptsForResolvedArtifact(
  artifact: ArtifactProto | undefined,
  tagKind: ArtifactKind,
  name: string
): ArtifactOpenOpts | undefined {
  if (artifact && getArtifactType(artifact) === ArtifactType.WORKFLOW_RUN) {
    const view = buildWorkflowRunViewState(artifact, WORKFLOW_RUN_TAB.outputs, name);
    return { activeTab: view.activeTab, subResourceName: name, kind: tagKind };
  }
  return getArtifactOpenOpts(tagKind, name);
}

/** Icon and display prefix for each artifact tag type. */
export const ARTIFACT_TAG_META: Record<string, { icon: keyof typeof AppIcons; prefix: string }> = {
  [ARTIFACT_TAG_TYPE.workflow]: { icon: "workflow", prefix: "Workflow" },
  [ARTIFACT_TAG_TYPE.dataframe]: { icon: "dataframe", prefix: "Table" },
  [ARTIFACT_TAG_TYPE.chart]: { icon: "chart", prefix: "Chart" },
  [ARTIFACT_TAG_TYPE.code]: { icon: "codeAlt", prefix: "Code" },
  [ARTIFACT_TAG_TYPE.report]: { icon: "document", prefix: "Document" },
  [ARTIFACT_TAG_TYPE.file]: { icon: "file", prefix: "File" },
};
